// src/hooks/useProfessionalData.ts - VERSIÓN CORREGIDA SIN CICLOS INFINITOS

import { useState, useEffect, useCallback, useRef } from 'react';

// =====================================================
// INTERFACES COMPLETAS
// =====================================================

interface Service {
  id: number;
  name: string;
  description: string;
  category: string;
  duration: number;
  price: number;
  requires_deposit: boolean;
  deposit_percentage?: number;
  deposit_amount?: number;
  is_active: boolean;
}

interface Appointment {
  id: number;
  professional_id: number;
  patient_id?: number;
  service_id: number;
  client_name?: string;
  client_email?: string;
  client_phone?: string;
  appointment_date: string;
  appointment_time: string;
  duration: number;
  price: number;
  deposit_required: boolean;
  deposit_amount?: number;
  deposit_paid: boolean;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';
  notes?: string;
  professional_notes?: string;
  is_first_time: boolean;
  urgency_level?: 'low' | 'normal' | 'high';
  cancelled_at?: string;
  cancellation_reason?: string;
  created_at: string;
  updated_at: string;
  service_name?: string;
  service_description?: string;
  service_category?: string;
  client_display_name?: string;
  client_display_email?: string;
  client_display_phone?: string;
  location_id?: number;
  location_name?: string;
  location_city?: string;
  patient_extra_data?: Record<string, any>;
}

interface DashboardMetrics {
  todayAppointments: number;
  weeklyAppointments: number;
  monthlyRevenue: number;
  pendingAppointments: number;
  completedAppointments: number;
  urgentCases: number;
  todayRevenue: number;
  totalClients: number;
  nextAppointment?: Appointment;
  professionalType: string;
  especialidad: string;
}

interface Professional {
  id: number;
  nombre: string;
  apellido: string;
  especialidad: string;
  professional_type: 'salud' | 'belleza';
  business_name?: string;
  picture?: string;
  cover_photo?: string;
  slug?: string;
}

interface UseProfessionalDataState {
  loading: boolean;
  loadingMetrics: boolean;
  loadingAppointments: boolean;
  loadingServices: boolean;
  metrics: DashboardMetrics | null;
  professional: Professional | null;
  appointments: Appointment[];
  services: Service[];
  error: string | null;
  metricsError: string | null;
  appointmentsError: string | null;
  servicesError: string | null;
  actionLoading: boolean;
  actionError: string | null;
}

interface UseProfessionalDataParams {
  professionalId?: number;
  autoLoad?: boolean;
  selectedDate?: string;
  enabled?: boolean;  // ← AGREGAR ESTA LÍNEA
}

// =====================================================
// CONFIGURACIÓN DE LA API
// =====================================================

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// =====================================================
// FUNCIÓN HELPER PARA REQUESTS CON TOKEN
// =====================================================

const apiRequest = async (url: string, options: RequestInit = {}) => {
  // Usar authFetch para manejo automático de token refresh en 401
  const { authFetch } = await import('../services/api');

  console.log('API Request:', options.method || 'GET', url);

  const response = await authFetch(url, options);

  if (!response.ok) {
    const error = await response.json();
    console.log('API Error', response.status + ':', error);
    throw new Error(error.error || error.message || 'Error en la petición');
  }

  return response.json();
};

// =====================================================
// FUNCIONES DE UTILIDAD PARA FECHAS
// =====================================================

const getToday = (): string => {
  return new Date().toISOString().split('T')[0];
};

const isDateInPast = (dateString: string): boolean => {
  const today = new Date();
  const appointmentDate = new Date(dateString);
  today.setHours(0, 0, 0, 0);
  appointmentDate.setHours(0, 0, 0, 0);
  return appointmentDate < today;
};

const isDateToday = (dateString: string): boolean => {
  const today = getToday();
  return dateString === today;
};

// =====================================================
// HOOK PRINCIPAL CORREGIDO
// =====================================================

export const useProfessionalData = (params: UseProfessionalDataParams = {}) => {
 const {
  professionalId,
  autoLoad = true,
  selectedDate,
  enabled = true  // ← AGREGAR ESTA LÍNEA
} = params;
  
  // Estado inicial. Arrancamos con los flags de loading en `enabled` para evitar
  // un flash de "estado vacio" en el primer render antes de que el useEffect
  // dispare la carga (UX: el medico/secre no ve "no hay consultas" por un
  // instante antes de que aparezcan los turnos reales).
  const [state, setState] = useState<UseProfessionalDataState>({
    loading: false,
    loadingMetrics: enabled,
    loadingAppointments: enabled,
    loadingServices: enabled,
    metrics: null,
    professional: null,
    appointments: [],
    services: [],
    error: null,
    metricsError: null,
    appointmentsError: null,
    servicesError: null,
    actionLoading: false,
    actionError: null,
  });

  // Ref para evitar múltiples cargas simultáneas
  const loadingRef = useRef(false);
  const lastLoadedId = useRef<number | null>(null);
  const lastLoadedDate = useRef<string | null>(null);

  // =====================================================
  // FUNCIONES AUXILIARES
  // =====================================================

  const updateState = useCallback((updates: Partial<UseProfessionalDataState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  // =====================================================
  // FUNCIÓN OPTIMIZADA: Cargar datos del profesional con fotos
  // =====================================================

  const loadProfessionalData = useCallback(async (profId: number) => {
    try {
      console.log(`📊 Cargando datos completos del profesional ${profId}...`);
      
      const response = await apiRequest(`${API_BASE_URL}/api/professionals/${profId}`);
      
      if (response.success && response.professional) {
        const professionalData = response.professional;
        
        // Actualizar localStorage con datos frescos de la BD
        const userData = localStorage.getItem('user');
        if (userData) {
          const currentUser = JSON.parse(userData);
          const updatedUser = { 
            ...currentUser, 
            picture: professionalData.picture,
            cover_photo: professionalData.cover_photo,
            business_name: professionalData.business_name,
            business_address: professionalData.business_address,
            business_description: professionalData.business_description
          };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          
          // Disparar evento para que otros componentes se actualicen
          window.dispatchEvent(new Event('userDataUpdated'));
        }
        
        updateState({ professional: professionalData });
        return professionalData;
      }
      
      return null;
      
    } catch (error: any) {
      console.error('Error cargando datos del profesional:', error);
      throw error;
    }
  }, [updateState]);

  // =====================================================
  // FUNCIÓN OPTIMIZADA: Cargar métricas
  // =====================================================

  const loadDashboardData = useCallback(async (profId: number, date?: string) => {
    updateState({ loadingMetrics: true, metricsError: null });
    
    try {
      console.log(`📊 Cargando métricas para profesional ${profId}, fecha: ${date || 'hoy'}...`);
      
      const params = date ? `?date=${date}` : '';
      const response = await apiRequest(`${API_BASE_URL}/api/appointments/professional/${profId}/metrics${params}`);
      
      updateState({
        metrics: response.metrics,
        loadingMetrics: false,
      });
      
      console.log('✅ Métricas cargadas exitosamente');
      
    } catch (error: any) {
      console.error('❌ Error cargando métricas:', error);
      updateState({
        loadingMetrics: false,
        metricsError: error.message || 'Error cargando métricas',
      });
    }
  }, [updateState]);

  // =====================================================
  // FUNCIÓN: Cargar citas
  // =====================================================

  const loadAppointments = useCallback(async (profId: number, date?: string, status?: string) => {
    updateState({ loadingAppointments: true, appointmentsError: null });
    
    try {
      console.log(`📅 Cargando citas para profesional ${profId}...`, { date, status });
      
      const params = new URLSearchParams();
      
      if (date) {
        params.append('date', date);
      }
      
      if (status && status !== 'todos') {
        params.append('status', status);
      }
      
      const queryString = params.toString();
      const endpoint = `${API_BASE_URL}/api/appointments/professional/${profId}${queryString ? `?${queryString}` : ''}`;
      
      const response = await apiRequest(endpoint);
      
      let filteredAppointments = response.appointments;
      
      if (!date) {
        const today = getToday();
        filteredAppointments = response.appointments.filter((apt: Appointment) => {
          return apt.appointment_date >= today;
        });
      }
      
      updateState({
        appointments: filteredAppointments,
        loadingAppointments: false,
      });
      
      console.log(`✅ ${filteredAppointments.length} citas cargadas exitosamente`);
      
    } catch (error: any) {
      console.error('❌ Error cargando citas:', error);
      updateState({
        loadingAppointments: false,
        appointmentsError: error.message || 'Error cargando citas',
      });
    }
  }, [updateState]);

  // =====================================================
  // FUNCIÓN: Cargar servicios
  // =====================================================

  const loadServices = useCallback(async (profId: number) => {
    updateState({ loadingServices: true, servicesError: null });
    
    try {
      console.log(`🛠️ Cargando servicios para profesional ${profId}...`);
      
      const response = await apiRequest(`${API_BASE_URL}/api/services/professional/${profId}/services`);
      
      updateState({
        services: response.services,
        loadingServices: false,
      });
      
      console.log(`✅ ${response.count || response.services.length} servicios cargados exitosamente`);
      
    } catch (error: any) {
      console.error('❌ Error cargando servicios:', error);
      updateState({
        loadingServices: false,
        servicesError: error.message || 'Error cargando servicios',
      });
    }
  }, [updateState]);

  // =====================================================
  // FUNCIÓN PRINCIPAL: Cargar todos los datos SIN DUPLICACIONES
  // =====================================================

  const refreshData = useCallback(async () => {
    if (!professionalId) {
      console.warn('⚠️ No se puede refrescar datos: professionalId no definido');
      return;
    }

    // Evitar múltiples cargas simultáneas
    if (loadingRef.current) {
      console.log('⏳ Ya hay una carga en progreso, ignorando...');
      return;
    }

    // Evitar recargas innecesarias del mismo profesional y fecha
    if (lastLoadedId.current === professionalId && lastLoadedDate.current === selectedDate) {
      console.log('📊 Datos ya están actualizados, saltando recarga...');
      return;
    }
    
    loadingRef.current = true;
    updateState({ loading: true, error: null });
    
    try {
      console.log('🔄 Iniciando carga completa de datos...');
      
      // 1. Cargar datos del profesional (incluyendo fotos)
      await loadProfessionalData(professionalId);
      
      // 2. Cargar métricas
      await loadDashboardData(professionalId, selectedDate);
      
      // 3. Cargar citas
      await loadAppointments(professionalId, selectedDate);
      
      // 4. Cargar servicios
      await loadServices(professionalId);
      
      // Marcar como cargado exitosamente
      lastLoadedId.current = professionalId;
      lastLoadedDate.current = selectedDate || getToday();
      
      updateState({ loading: false });
      console.log('✅ Todos los datos cargados exitosamente');
      
    } catch (error: any) {
      console.error('❌ Error en carga completa:', error);
      updateState({
        loading: false,
        error: 'Error cargando los datos. Por favor intenta nuevamente.',
      });
    } finally {
      loadingRef.current = false;
    }
  }, [professionalId, selectedDate, loadProfessionalData, loadDashboardData, loadAppointments, loadServices, updateState]);

  // =====================================================
  // FUNCIÓN: Actualizar estado de cita
  // =====================================================

  const updateAppointmentStatus = useCallback(async (
    profId: number, 
    appointmentId: number, 
    status: string, 
    notes?: string,
    cancellationReason?: string
  ): Promise<boolean> => {
    updateState({ actionLoading: true, actionError: null });
    
    try {
      console.log(`📝 Actualizando estado de cita ${appointmentId} a ${status}...`);
      
      const requestBody = {
        status,
        ...(notes && { professionalNotes: notes }),
        ...(cancellationReason && { cancellationReason })
      };
      
      await apiRequest(`${API_BASE_URL}/api/appointments/professional/${profId}/appointment/${appointmentId}`, {
        method: 'PUT',
        body: JSON.stringify(requestBody),
      });
      
      // Actualizar la cita en la lista local
      setState(prev => ({
        ...prev,
        appointments: prev.appointments.map((apt: Appointment) =>
          apt.id === appointmentId 
            ? { ...apt, status: status as any, professional_notes: notes }
            : apt
        ),
        actionLoading: false,
      }));
      
      // Solo recargar métricas (sin datos completos para evitar ciclos)
      await loadDashboardData(profId, selectedDate);
      
      console.log('✅ Estado de cita actualizado exitosamente');
      return true;
      
    } catch (error: any) {
      console.error('❌ Error actualizando estado de cita:', error);
      updateState({
        actionLoading: false,
        actionError: error.message || 'Error actualizando cita',
      });
      return false;
    }
  }, [updateState, loadDashboardData, selectedDate]);

  // =====================================================
  // FUNCIÓN: Reagendar cita
  // =====================================================

  const rescheduleAppointment = useCallback(async (
    profId: number,
    appointmentId: number,
    newDate: string,
    newTime: string,
    reason?: string
  ): Promise<boolean> => {
    updateState({ actionLoading: true, actionError: null });
    
    try {
      console.log(`📅 Reagendando cita ${appointmentId}...`);
      
      const requestBody = {
        newDate,
        newTime,
        ...(reason && { reason })
      };
      
      await apiRequest(`${API_BASE_URL}/api/appointments/professional/${profId}/appointment/${appointmentId}/reschedule`, {
        method: 'PUT',
        body: JSON.stringify(requestBody),
      });
      
      // Actualizar la cita en la lista local
      setState(prev => ({
        ...prev,
        appointments: prev.appointments.map((apt: Appointment) =>
          apt.id === appointmentId 
            ? { 
                ...apt, 
                appointment_date: newDate, 
                appointment_time: newTime,
                status: 'pending' as any 
              }
            : apt
        ),
        actionLoading: false,
      }));
      
      // Recargar solo métricas y citas
      await Promise.all([
        loadDashboardData(profId, selectedDate),
        loadAppointments(profId, selectedDate)
      ]);
      
      console.log('✅ Cita reagendada exitosamente');
      return true;
      
    } catch (error: any) {
      console.error('❌ Error reagendando cita:', error);
      updateState({
        actionLoading: false,
        actionError: error.message || 'Error reagendando cita',
      });
      return false;
    }
  }, [updateState, loadDashboardData, loadAppointments, selectedDate]);

  // =====================================================
  // FUNCIÓN: Determinar acciones disponibles para una cita
  // =====================================================

  const getAvailableActions = useCallback((appointment: Appointment) => {
    const isInPast = isDateInPast(appointment.appointment_date);
    const isToday = isDateToday(appointment.appointment_date);
    
    const actions: string[] = [];
    
    if (!isInPast) {
      switch (appointment.status) {
        case 'pending':
          actions.push('confirmar', 'cancelar', 'reagendar');
          break;
        case 'confirmed':
          if (isToday) {
            actions.push('completar', 'cancelar', 'reagendar');
          } else {
            actions.push('cancelar', 'reagendar');
          }
          break;
        case 'cancelled':
          if (!isInPast) {
            actions.push('reagendar');
          }
          break;
      }
    } else {
      if (appointment.status === 'pending' || appointment.status === 'confirmed') {
        actions.push('mark_no_show');
      }
    }
    
    return actions;
  }, []);

  // =====================================================
  // FUNCIONES DE UTILIDAD
  // =====================================================

  const clearErrors = useCallback(() => {
    updateState({
      error: null,
      metricsError: null,
      appointmentsError: null,
      servicesError: null,
      actionError: null,
    });
  }, [updateState]);

  const clearActionState = useCallback(() => {
    updateState({
      actionLoading: false,
      actionError: null,
    });
  }, [updateState]);

  // =====================================================
  // EFECTOS OPTIMIZADOS
  // =====================================================

  // Carga automática inicial SOLO UNA VEZ
  useEffect(() => {
    if (autoLoad && professionalId && enabled) {
      console.log(`🚀 Carga inicial para profesional ${professionalId}`);
      refreshData();
    }
  }, [autoLoad, professionalId]); // Solo depende de professionalId, NO de refreshData

  // Recarga cuando cambia la fecha SOLO si ya tenemos datos del profesional
  useEffect(() => {
    if (professionalId && selectedDate && state.professional && !loadingRef.current) {
      // Solo recargar si la fecha realmente cambió
      if (lastLoadedDate.current !== selectedDate) {
        console.log(`📅 Cambiando datos para fecha: ${selectedDate}`);
        loadDashboardData(professionalId, selectedDate);
        loadAppointments(professionalId, selectedDate);
        lastLoadedDate.current = selectedDate;
      }
    }
  }, [selectedDate, professionalId, state.professional]); // NO incluir loadDashboardData

  // Escuchar cambios en localStorage SIN RECARGAR TODO
  useEffect(() => {
    const handleUserDataUpdate = () => {
      try {
        const userData = localStorage.getItem('user');
        if (userData && professionalId) {
          const parsedUser = JSON.parse(userData);
          
          // Solo actualizar si es el mismo profesional
          if (parsedUser.id === professionalId) {
            updateState({
              professional: {
                id: parsedUser.id,
                nombre: parsedUser.nombre,
                apellido: parsedUser.apellido,
                especialidad: parsedUser.especialidad,
                professional_type: parsedUser.professional_type,
                business_name: parsedUser.business_name,
                picture: parsedUser.picture,
                cover_photo: parsedUser.cover_photo
              }
            });
            
            console.log('🔄 Professional data actualizado desde localStorage');
          }
        }
      } catch (error) {
        console.error('Error actualizando datos desde localStorage:', error);
      }
    };

    // Escuchar evento personalizado (no storage event que puede crear loops)
    window.addEventListener('userDataUpdated', handleUserDataUpdate);

    return () => {
      window.removeEventListener('userDataUpdated', handleUserDataUpdate);
    };
  }, [professionalId, updateState]);

  // =====================================================
  // RETORNO DEL HOOK
  // =====================================================

  return {
    // Estados
    ...state,
    
    // Acciones principales
    loadDashboardData,
    loadAppointments,
    loadServices,
    loadProfessionalData,
    refreshData,
    updateAppointmentStatus,
    rescheduleAppointment,
    
    // Utilidades
    clearErrors,
    clearActionState,
    getAvailableActions,
    
    // Utilidades de fecha
    getToday,
    isDateInPast,
    isDateToday,
  };
};